import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  TABLE_FILTER_ALL,
  useStore,
  type StatusBucket,
  type TableFilter,
} from '../store';
import type { Page } from '../types';
import { formatBytes, formatMs } from '../utils/format';
import { PageDetailPanel } from './PageDetailPanel';

type SortKey =
  | 'url'
  | 'status'
  | 'title'
  | 'titleLength'
  | 'descLength'
  | 'lang'
  | 'h1'
  | 'h1Count'
  | 'h2Count'
  | 'h3Count'
  | 'ttfb'
  | 'size'
  | 'imageCount'
  | 'imagesNoAlt'
  | 'sdCount'
  | 'canonicalStatus'
  | 'externalLinkCount'
  | 'internalLinkCount';
type SortDir = 'asc' | 'desc';

// ページ単位の問題判定。WarningsSummary と判定基準を揃える
function isAltMissing(p: Page): boolean {
  return (p.images?.filter((img) => img.alt === undefined || img.alt === '').length ?? 0) > 0;
}
function isH1Abnormal(p: Page): boolean {
  return (p.headings?.h1.length ?? 1) !== 1;
}
function isCanonicalBroken(p: Page): boolean {
  return p.canonicalCheck !== undefined && p.canonicalCheck.status >= 400;
}
function isTitleMissing(p: Page): boolean {
  return !p.title;
}
function hasWarning(p: Page): boolean {
  return (
    isAltMissing(p) ||
    isH1Abnormal(p) ||
    isCanonicalBroken(p) ||
    isTitleMissing(p) ||
    !p.metaDescription
  );
}

function statusBucket(p: Page): StatusBucket {
  if (p.error) return 'error';
  if (p.status >= 500) return '5xx';
  if (p.status >= 400) return '4xx';
  if (p.status >= 300) return '3xx';
  if (p.status >= 200) return '2xx';
  return 'error';
}

// フィルタ選択中に "今何で絞り込まれているか / なぜ重要か" を説明するバナーを出す。
// 「成功」「リダイレクト」など短いラベルだけでは意味が伝わらないため、補足を必ず添える
type FilterExplanation = {
  label: string;
  tone: 'emerald' | 'amber' | 'red' | 'blue' | 'slate';
  description: string;
};

function getFilterExplanation(filter: TableFilter): FilterExplanation | null {
  if (filter.kind === 'all') return null;
  if (filter.kind === 'status') {
    switch (filter.status) {
      case '2xx':
        return {
          label: '成功 (2xx)',
          tone: 'emerald',
          description:
            'HTTP ステータス 200 番台。クロールが正常完了したページ。通常は問題なし。タイトルや構造化データ等の品質チェックは "要確認" の方で',
        };
      case '3xx':
        return {
          label: 'リダイレクト (3xx)',
          tone: 'amber',
          description:
            'HTTP ステータス 300 番台（301/302 などの転送）。最終 URL が正しいか・転送が無駄に連鎖していないかを確認',
        };
      case '4xx':
        return {
          label: 'クライアントエラー (4xx)',
          tone: 'red',
          description:
            'HTTP ステータス 400 番台（404 Not Found など）。リンク切れ・削除済みページ。SEO 的にも修正対象',
        };
      case '5xx':
        return {
          label: 'サーバーエラー (5xx)',
          tone: 'red',
          description:
            'HTTP ステータス 500 番台（サーバー側の障害）。一時的か恒常的かの切り分けが必要',
        };
      case 'error':
        return {
          label: 'クロール失敗',
          tone: 'red',
          description:
            'DNS 失敗・接続拒否・タイムアウト等で取得できなかったページ。ネットワークやサーバー設定の問題が疑われる',
        };
    }
  }
  if (filter.kind === 'warning') {
    switch (filter.warning) {
      case 'all':
        return {
          label: '要確認',
          tone: 'amber',
          description:
            'alt 欠 / h1 異常 / canonical 壊 / title 欠 / desc 欠 のいずれかに該当するページ。ステータスとは別軸の "品質" シグナル',
        };
      case 'altMissing':
        return {
          label: 'alt 欠',
          tone: 'amber',
          description:
            'alt 属性なし or 空文字の画像を含むページ。SEO・アクセシビリティの両面で要確認',
        };
      case 'h1Abnormal':
        return {
          label: 'h1 異常',
          tone: 'amber',
          description:
            'h1 が 0 個または複数あるページ。各ページに 1 個の h1 が SEO ベストプラクティス',
        };
      case 'canonicalBroken':
        return {
          label: 'canonical 壊',
          tone: 'red',
          description:
            'canonical が指す先が 4xx 以上のページ。リンク先 URL の修正が必要',
        };
      case 'titleMissing':
        return {
          label: 'title 欠',
          tone: 'amber',
          description:
            'title 未設定のページ。検索結果・タブ表示・SNS シェアに使われる最重要要素',
        };
    }
  }
  return null;
}

export function TableView() {
  const data = useStore((s) => s.data);
  const tableFilter = useStore((s) => s.tableFilter);
  const setTableFilter = useStore((s) => s.setTableFilter);

  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('url');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  // 右ドロワーの選択 URL。初期は先頭ページが入る（useEffect で自動）
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  // 右ドロワーの幅 (px)。ハンドルドラッグで左右に伸縮
  const [detailWidth, setDetailWidth] = useState(520);
  const isDraggingRef = useRef(false);

  // URL 列の幅 (px)。列ヘッダ右端のハンドルで変更可能、localStorage で永続化
  const [urlColumnWidth, setUrlColumnWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return 224;
    const saved = window.localStorage.getItem('viewer.urlColumnWidth');
    const n = saved ? Number(saved) : NaN;
    return Number.isFinite(n) && n >= 100 ? n : 224;
  });
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('viewer.urlColumnWidth', String(urlColumnWidth));
    }
  }, [urlColumnWidth]);
  const urlResizeRef = useRef<{ startX: number; startW: number } | null>(null);

  // URL 列リサイズハンドルのドラッグ管理
  useEffect(() => {
    const onMove = (e: MouseEvent): void => {
      const s = urlResizeRef.current;
      if (!s) return;
      const next = Math.max(100, Math.min(900, s.startW + (e.clientX - s.startX)));
      setUrlColumnWidth(next);
    };
    const onUp = (): void => {
      urlResizeRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  // クロール起点のホスト。同一ホストの URL はテーブル表示時にドメインを省略してパスのみ見せる
  const primaryHost = useMemo<string | undefined>(() => {
    if (!data) return undefined;
    try {
      return new URL(data.crawl.seedUrl).host;
    } catch {
      return undefined;
    }
  }, [data]);

  // 上下のスクロールバーを同期させるための ref。
  // 行数が多いと下のスクロールバーが画面外に行ってしまうので、上にもミラー設置する
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const topScrollRef = useRef<HTMLDivElement>(null);
  const [tableScrollWidth, setTableScrollWidth] = useState(0);

  // ハンドルのマウスドラッグでドロワー幅を更新（画面右端からの距離 = ドロワー幅）
  useEffect(() => {
    const onMove = (e: MouseEvent): void => {
      if (!isDraggingRef.current) return;
      const vw = window.innerWidth;
      const next = Math.max(320, Math.min(vw - 480, vw - e.clientX));
      setDetailWidth(next);
    };
    const onUp = (): void => {
      isDraggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  useEffect(() => {
    const container = tableScrollRef.current;
    if (!container) return;
    const update = (): void => {
      const t = container.querySelector('table');
      if (t) setTableScrollWidth(t.scrollWidth);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(container);
    const t = container.querySelector('table');
    if (t) ro.observe(t);
    return () => ro.disconnect();
  }, []);

  // 全ページに対する集計（フィルタ前）。チップの件数表示用
  const counts = useMemo(() => {
    if (!data) return null;
    const status: Record<StatusBucket, number> = {
      '2xx': 0,
      '3xx': 0,
      '4xx': 0,
      '5xx': 0,
      error: 0,
    };
    let warnings = 0;
    for (const p of data.pages) {
      status[statusBucket(p)]++;
      if (hasWarning(p)) warnings++;
    }
    return { all: data.pages.length, status, warnings };
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const f = filter.trim().toLowerCase();
    let pages = data.pages;
    if (tableFilter.kind === 'status') {
      pages = pages.filter((p) => statusBucket(p) === tableFilter.status);
    } else if (tableFilter.kind === 'warning') {
      switch (tableFilter.warning) {
        case 'all':
          pages = pages.filter(hasWarning);
          break;
        case 'altMissing':
          pages = pages.filter(isAltMissing);
          break;
        case 'h1Abnormal':
          pages = pages.filter(isH1Abnormal);
          break;
        case 'canonicalBroken':
          pages = pages.filter(isCanonicalBroken);
          break;
        case 'titleMissing':
          pages = pages.filter(isTitleMissing);
          break;
      }
    }
    if (f) {
      pages = pages.filter(
        (p) =>
          p.url.toLowerCase().includes(f) ||
          (p.title?.toLowerCase().includes(f) ?? false),
      );
    }
    const sorted = [...pages].sort((a, b) => compare(a, b, sortKey));
    return sortDir === 'asc' ? sorted : sorted.reverse();
  }, [data, filter, tableFilter, sortKey, sortDir]);

  // 初期表示時 / フィルタ変更で選択が無効になった時は、先頭ページを自動選択
  useEffect(() => {
    const first = filtered[0];
    if (!first) {
      if (selectedUrl !== null) setSelectedUrl(null);
      return;
    }
    if (!selectedUrl || !filtered.some((p) => p.url === selectedUrl)) {
      setSelectedUrl(first.url);
    }
  }, [filtered, selectedUrl]);

  if (!data || !counts) return <></>;

  const toggle = (key: SortKey): void => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const isStatusActive = (s: StatusBucket): boolean =>
    tableFilter.kind === 'status' && tableFilter.status === s;
  // 要確認チップは「要確認カテゴリのいずれか（all 含む）」で active 扱い。
  // 詳細サブフィルタが当たっているときも 要確認 はハイライト（親子の整合）
  const isWarningCategoryActive = (): boolean => tableFilter.kind === 'warning';

  return (
    <div
      className="flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white"
      style={{ height: 'calc(100vh - 180px)' }}
    >
      <div className="shrink-0 flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 px-5 py-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {/* 「全て」は親（無条件）。視覚的に少し大きめ・濃いめにして他の絞り込みチップと区別する */}
          <button
            onClick={() => setTableFilter(TABLE_FILTER_ALL)}
            className={`inline-flex items-baseline gap-1.5 rounded-md border px-2 py-1 text-sm font-semibold transition ${
              tableFilter.kind === 'all'
                ? 'border-slate-800 bg-slate-700 text-white'
                : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            全て
            <span className="tabular-nums opacity-80">{counts.all}</span>
          </button>

          {/* ステータス軸グループ */}
          <div className="flex items-center gap-2 border-l border-slate-200 pl-4">
            <span className="hidden text-[10px] font-medium uppercase tracking-wide text-slate-400 sm:inline">
              ステータス
            </span>
            <FilterChip
              label="成功"
              sub="2xx"
              count={counts.status['2xx']}
              active={isStatusActive('2xx')}
              onClick={() => setTableFilter({ kind: 'status', status: '2xx' })}
              tone="emerald"
              title="ステータス 200 番台（正常レスポンス）"
            />
            <FilterChip
              label="リダイレクト"
              sub="3xx"
              count={counts.status['3xx']}
              active={isStatusActive('3xx')}
              onClick={() => setTableFilter({ kind: 'status', status: '3xx' })}
              tone="amber"
              title="ステータス 300 番台（最終的に解決される転送）"
            />
            <FilterChip
              label="クライアント"
              sub="4xx"
              count={counts.status['4xx']}
              active={isStatusActive('4xx')}
              onClick={() => setTableFilter({ kind: 'status', status: '4xx' })}
              tone="red"
              title="ステータス 400 番台（404 Not Found など。要確認）"
            />
            <FilterChip
              label="サーバー"
              sub="5xx"
              count={counts.status['5xx']}
              active={isStatusActive('5xx')}
              onClick={() => setTableFilter({ kind: 'status', status: '5xx' })}
              tone="red"
              title="ステータス 500 番台（サーバー側の障害）"
            />
            {counts.status.error > 0 && (
              <FilterChip
                label="クロール失敗"
                count={counts.status.error}
                active={isStatusActive('error')}
                onClick={() => setTableFilter({ kind: 'status', status: 'error' })}
                tone="red"
                title="DNS 失敗・タイムアウト等で取得できなかったページ"
              />
            )}
          </div>

          {/* 品質軸グループ。ステータスとは別軸であることを区切り線とラベルで示す */}
          <div className="flex items-center gap-2 border-l border-slate-200 pl-4">
            <span className="hidden text-[10px] font-medium uppercase tracking-wide text-slate-400 sm:inline">
              品質
            </span>
            <FilterChip
              label="要確認"
              count={counts.warnings}
              active={isWarningCategoryActive()}
              onClick={() => setTableFilter({ kind: 'warning', warning: 'all' })}
              tone="amber"
              title="alt 欠 / h1 異常 / canonical 壊 / title 欠 / desc 欠 のいずれかに該当するページ（ステータスとは別軸）。上の要確認バーで詳細指定も可能"
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">
            表示 <span className="tabular-nums font-medium text-slate-700">{filtered.length}</span> 件
          </span>
          <input
            type="search"
            placeholder="URL またはタイトル"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-56 rounded-md border border-slate-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
          />
        </div>
      </div>

      {/* どのフィルタが当たっているか・なぜ重要かを説明するバナー。
          短いラベルだけでは意味が伝わらないため、必ず一文の補足を添える */}
      {(() => {
        const exp = getFilterExplanation(tableFilter);
        if (!exp) return null;
        const toneCls = {
          emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
          amber: 'border-amber-200 bg-amber-50 text-amber-800',
          red: 'border-red-200 bg-red-50 text-red-800',
          blue: 'border-blue-200 bg-blue-50 text-blue-800',
          slate: 'border-slate-200 bg-slate-50 text-slate-700',
        }[exp.tone];
        const buttonCls = {
          emerald: 'border-emerald-300 text-emerald-700 hover:bg-emerald-100',
          amber: 'border-amber-300 text-amber-700 hover:bg-amber-100',
          red: 'border-red-300 text-red-700 hover:bg-red-100',
          blue: 'border-blue-300 text-blue-700 hover:bg-blue-100',
          slate: 'border-slate-300 text-slate-700 hover:bg-slate-100',
        }[exp.tone];
        return (
          <div className={`shrink-0 flex items-baseline gap-3 border-b px-5 py-2 text-xs ${toneCls}`}>
            <span className="shrink-0 font-semibold">{exp.label}</span>
            <span className="text-[11px] opacity-90">{exp.description}</span>
            <button
              onClick={() => setTableFilter(TABLE_FILTER_ALL)}
              className={`ml-auto shrink-0 rounded border bg-white px-2 py-0.5 text-[11px] ${buttonCls}`}
            >
              ✕ 全件に戻す
            </button>
          </div>
        );
      })()}

      {/* 左右並びコンテナ。左 = テーブル領域、右 = 常時表示の詳細ペイン */}
      <div className="flex flex-1 overflow-hidden">
      <div className="flex flex-1 flex-col overflow-hidden">
      {/* 上ミラーのスクロールバー。長い表でも降りずに横スクロール可能にする */}
      <div
        ref={topScrollRef}
        onScroll={() => {
          const top = topScrollRef.current;
          const main = tableScrollRef.current;
          if (top && main && main.scrollLeft !== top.scrollLeft) {
            main.scrollLeft = top.scrollLeft;
          }
        }}
        className="shrink-0 overflow-x-scroll border-b border-slate-200"
        style={{ height: 14 }}
      >
        <div style={{ width: tableScrollWidth, height: 1 }} />
      </div>
      <div
        ref={tableScrollRef}
        onScroll={() => {
          const top = topScrollRef.current;
          const main = tableScrollRef.current;
          if (top && main && top.scrollLeft !== main.scrollLeft) {
            top.scrollLeft = main.scrollLeft;
          }
        }}
        // 縦方向にも overflow を持たせ、内側で thead を sticky させる。
        // 親が flex-1 で高さを与えているため、ここも flex-1 で残り高さを占有
        className="relative flex-1 overflow-auto"
      >
        <table className="w-full text-sm">
          <thead className="border-b border-slate-700 bg-slate-700 text-xs uppercase text-white [&_th]:border-r [&_th]:border-slate-500 [&_th:last-child]:border-r-0">
            <tr>
              <UrlTh
                sortKey={sortKey}
                sortDir={sortDir}
                toggle={toggle}
                width={urlColumnWidth}
                onResizeStart={(startX) => {
                  urlResizeRef.current = { startX, startW: urlColumnWidth };
                  document.body.style.cursor = 'col-resize';
                  document.body.style.userSelect = 'none';
                }}
              />
              <Th label="status" k="status" sortKey={sortKey} sortDir={sortDir} toggle={toggle} align="center" />
              <Th label="title" k="title" sortKey={sortKey} sortDir={sortDir} toggle={toggle} />
              <Th label="title 字数" k="titleLength" sortKey={sortKey} sortDir={sortDir} toggle={toggle} align="right" title="title の文字数" />
              <Th label="desc 字数" k="descLength" sortKey={sortKey} sortDir={sortDir} toggle={toggle} align="right" title="metaDescription の文字数" />
              <Th label="lang" k="lang" sortKey={sortKey} sortDir={sortDir} toggle={toggle} align="center" title="<html lang> 属性" />
              <Th label="h1" k="h1" sortKey={sortKey} sortDir={sortDir} toggle={toggle} title="最初の h1 のテキスト" />
              <Th label="h1数" k="h1Count" sortKey={sortKey} sortDir={sortDir} toggle={toggle} align="right" title="h1 の数（複数あれば SEO 観点で要確認）" />
              <Th label="h2数" k="h2Count" sortKey={sortKey} sortDir={sortDir} toggle={toggle} align="right" />
              <Th label="h3数" k="h3Count" sortKey={sortKey} sortDir={sortDir} toggle={toggle} align="right" />
              <Th label="TTFB" k="ttfb" sortKey={sortKey} sortDir={sortDir} toggle={toggle} align="right" />
              <Th label="size" k="size" sortKey={sortKey} sortDir={sortDir} toggle={toggle} align="right" />
              <Th label="img" k="imageCount" sortKey={sortKey} sortDir={sortDir} toggle={toggle} align="right" title="ページ内の <img> タグ数" />
              <Th label="alt欠" k="imagesNoAlt" sortKey={sortKey} sortDir={sortDir} toggle={toggle} align="right" title="alt 属性なし or 空文字の画像数（SEO・アクセシビリティ要確認）" />
              <Th label="JSON-LD" k="sdCount" sortKey={sortKey} sortDir={sortDir} toggle={toggle} align="right" title="JSON-LD（構造化データ）ブロック数" />
              <Th label="canonical先" k="canonicalStatus" sortKey={sortKey} sortDir={sortDir} toggle={toggle} align="center" title="canonical 先 URL のステータスコード（200 以外は赤マーク）" />
              <Th label="外部L" k="externalLinkCount" sortKey={sortKey} sortDir={sortDir} toggle={toggle} align="right" title="外部リンク数" />
              <Th label="内部L" k="internalLinkCount" sortKey={sortKey} sortDir={sortDir} toggle={toggle} align="right" title="内部リンク数" />
              <th className="sticky top-0 z-20 bg-slate-700 px-2 py-1 text-left text-xs" title="JS 描画されたページ">JS</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const titleLen = p.title?.length ?? 0;
              const descLen = p.metaDescription?.length ?? 0;
              const h1Count = p.headings?.h1.length ?? 0;
              const h2Count = p.headings?.h2.length ?? 0;
              const h3Count = p.headings?.h3.length ?? 0;
              const imageCount = p.images?.length ?? 0;
              const imagesNoAlt =
                p.images?.filter((img) => img.alt === undefined || img.alt === '').length ?? 0;
              const sdCount = p.structuredData?.length ?? 0;
              const cc = p.canonicalCheck;
              const externalLinkCount = p.externalLinks?.length ?? 0;
              const internalLinkCount = p.internalLinks?.length ?? 0;
              const h1Text = p.headings?.h1[0] ?? '';
              const isSelected = selectedUrl === p.url;
              return (
                <tr
                  key={p.url}
                  onClick={() => setSelectedUrl(p.url)}
                  className={`cursor-pointer border-b border-slate-100 ${
                    isSelected ? 'bg-blue-50 hover:bg-blue-50' : 'hover:bg-slate-50'
                  }`}
                  title="クリックで詳細パネルを開く"
                >
                  <td
                    style={{ width: urlColumnWidth, minWidth: urlColumnWidth, maxWidth: urlColumnWidth }}
                    className={`sticky left-0 z-10 px-2 py-1 font-mono text-xs ${
                      isSelected ? 'bg-blue-50' : 'bg-white hover:bg-slate-50'
                    }`}
                  >
                    <UrlCell url={p.url} primaryHost={primaryHost} />
                  </td>
                  <td className="px-2 py-1 text-center">
                    <span
                      className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${statusColor(p.status, p.error)}`}
                    >
                      {p.error ?? p.status}
                    </span>
                  </td>
                  <td className="max-w-[16rem] truncate px-2 py-1 text-slate-700" title={p.title}>
                    {p.title ?? '-'}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-slate-600">
                    {p.title ? titleLen : '-'}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-slate-600">
                    {p.metaDescription ? descLen : '-'}
                  </td>
                  <td className="px-2 py-1 text-center text-xs text-slate-600">
                    {p.lang ?? '-'}
                  </td>
                  <td className="max-w-[16rem] px-2 py-1 text-slate-700">
                    <div className="flex items-baseline gap-1.5">
                      <span
                        className="min-w-0 flex-1 truncate"
                        title={
                          h1Count > 1
                            ? `h1 が ${h1Count} 個あります:\n${p.headings?.h1.join('\n')}`
                            : h1Text
                        }
                      >
                        {h1Text || '-'}
                      </span>
                      {h1Count > 1 && (
                        <span
                          className="shrink-0 rounded bg-amber-100 px-1 py-0.5 text-[9px] font-medium text-amber-700"
                          title={`他に ${h1Count - 1} 個の h1:\n${p.headings?.h1.slice(1).join('\n')}`}
                        >
                          +{h1Count - 1}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1 text-right">
                    {p.headings ? (
                      <PopoverCell
                        count={h1Count}
                        items={p.headings.h1}
                        emphasize={h1Count !== 1}
                        heading="h1"
                        title={
                          h1Count > 1
                            ? 'h1 が複数あります（SEO 観点で要確認）。クリックで一覧表示'
                            : 'クリックで h1 テキストを表示'
                        }
                      />
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-2 py-1 text-right">
                    {p.headings ? (
                      <PopoverCell
                        count={h2Count}
                        items={p.headings.h2}
                        heading="h2"
                        title="クリックで h2 一覧表示"
                      />
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-2 py-1 text-right">
                    {p.headings ? (
                      <PopoverCell
                        count={h3Count}
                        items={p.headings.h3}
                        heading="h3"
                        title="クリックで h3 一覧表示"
                      />
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-slate-700">
                    {p.performance ? formatMs(p.performance.ttfb) : '-'}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-slate-700">
                    {p.performance ? formatBytes(p.performance.htmlBytes) : '-'}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-slate-700">
                    {p.images === undefined ? '-' : imageCount}
                  </td>
                  <td
                    className={`px-2 py-1 text-right tabular-nums ${
                      imagesNoAlt > 0 ? 'text-amber-700 font-medium' : 'text-slate-700'
                    }`}
                    title={
                      imagesNoAlt > 0
                        ? `alt 属性なし or 空文字の画像が ${imagesNoAlt} 枚`
                        : undefined
                    }
                  >
                    {p.images === undefined ? '-' : imagesNoAlt}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-slate-700">
                    {p.structuredData === undefined ? '-' : sdCount}
                  </td>
                  <td
                    className="px-2 py-1 text-center"
                    title={
                      cc
                        ? `canonical 先: ${cc.finalUrl} (status ${cc.status})`
                        : 'canonical 未設定または未確認'
                    }
                  >
                    {cc ? (
                      <span
                        className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${statusColor(cc.status)}`}
                      >
                        {cc.status}
                      </span>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-slate-700">
                    {externalLinkCount}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-slate-700">
                    {p.internalLinks === undefined ? '-' : internalLinkCount}
                  </td>
                  <td className="px-2 py-1 text-xs text-slate-500">
                    {p.jsRendered ? '✓' : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="px-4 py-12 text-center text-sm text-slate-500">
            条件に一致するページがありません
          </div>
        )}
      </div>
      </div>

      {/* 左右分割のドラッグハンドル */}
      <div
        onMouseDown={(e) => {
          isDraggingRef.current = true;
          document.body.style.cursor = 'col-resize';
          document.body.style.userSelect = 'none';
          e.preventDefault();
        }}
        className="shrink-0 flex w-1.5 cursor-col-resize flex-col items-center justify-center border-x border-slate-200 bg-slate-100 hover:bg-blue-100"
        title="ドラッグで詳細パネルの幅を変更"
        role="separator"
        aria-orientation="vertical"
      >
        <div className="h-10 w-0.5 rounded-full bg-slate-400" />
      </div>

      {/* 右に常時表示する詳細パネル */}
      <div className="shrink-0 overflow-hidden" style={{ width: detailWidth }}>
        <PageDetailPanel
          page={selectedUrl ? data.pages.find((p) => p.url === selectedUrl) ?? null : null}
        />
      </div>
      </div>
    </div>
  );
}

function FilterChip({
  label,
  sub,
  count,
  active,
  onClick,
  tone = 'slate',
  title,
}: {
  label: string;
  sub?: string;
  count: number;
  active: boolean;
  onClick: () => void;
  tone?: 'slate' | 'emerald' | 'amber' | 'red';
  title?: string;
}) {
  // 件数 0 のチップは押しても意味がないので非活性にする
  const disabled = count === 0;
  const activeColors = {
    slate: 'border-slate-700 bg-slate-700 text-white',
    emerald: 'border-emerald-600 bg-emerald-600 text-white',
    amber: 'border-amber-600 bg-amber-600 text-white',
    red: 'border-red-600 bg-red-600 text-white',
  }[tone];
  const inactiveColors = {
    slate: 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
    amber: 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100',
    red: 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100',
  }[tone];
  const disabledColors = 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed';
  const colors = disabled ? disabledColors : active ? activeColors : inactiveColors;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={disabled ? `${label}: 該当ページなし` : title}
      className={`inline-flex items-baseline gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition ${colors}`}
    >
      <span>{label}</span>
      {sub && <span className="text-[10px] opacity-70">{sub}</span>}
      <span className="tabular-nums">{count}</span>
    </button>
  );
}

function Th({
  label,
  k,
  sortKey,
  sortDir,
  toggle,
  align,
  title,
  sticky,
}: {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  toggle: (k: SortKey) => void;
  align?: 'left' | 'center' | 'right';
  title?: string;
  sticky?: boolean;
}) {
  const cls = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  const arrow = sortKey === k ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  // 全 th を sticky top に。URL 列のみさらに sticky left も併用（左上で固定）
  // 反転配色（濃い背景に白文字）で見出し帯を目立たせる
  const stickyClass = sticky
    ? 'sticky left-0 top-0 z-30 bg-slate-700'
    : 'sticky top-0 z-20 bg-slate-700';
  return (
    <th
      onClick={() => toggle(k)}
      className={`cursor-pointer select-none whitespace-nowrap px-2 py-1 ${cls} ${stickyClass} font-medium hover:bg-slate-600`}
      title={title}
    >
      {label}
      {arrow}
    </th>
  );
}

function compare(a: Page, b: Page, key: SortKey): number {
  switch (key) {
    case 'url':
      return a.url.localeCompare(b.url);
    case 'status':
      return a.status - b.status;
    case 'title':
      return (a.title ?? '').localeCompare(b.title ?? '');
    case 'titleLength':
      return (a.title?.length ?? -1) - (b.title?.length ?? -1);
    case 'descLength':
      return (a.metaDescription?.length ?? -1) - (b.metaDescription?.length ?? -1);
    case 'lang':
      return (a.lang ?? '').localeCompare(b.lang ?? '');
    case 'h1':
      return (a.headings?.h1[0] ?? '').localeCompare(b.headings?.h1[0] ?? '');
    case 'h1Count':
      return (a.headings?.h1.length ?? -1) - (b.headings?.h1.length ?? -1);
    case 'h2Count':
      return (a.headings?.h2.length ?? -1) - (b.headings?.h2.length ?? -1);
    case 'h3Count':
      return (a.headings?.h3.length ?? -1) - (b.headings?.h3.length ?? -1);
    case 'ttfb':
      return (a.performance?.ttfb ?? -1) - (b.performance?.ttfb ?? -1);
    case 'size':
      return (a.performance?.htmlBytes ?? -1) - (b.performance?.htmlBytes ?? -1);
    case 'imageCount':
      return (a.images?.length ?? -1) - (b.images?.length ?? -1);
    case 'imagesNoAlt': {
      const aa =
        a.images?.filter((img) => img.alt === undefined || img.alt === '').length ?? -1;
      const bb =
        b.images?.filter((img) => img.alt === undefined || img.alt === '').length ?? -1;
      return aa - bb;
    }
    case 'sdCount':
      return (a.structuredData?.length ?? -1) - (b.structuredData?.length ?? -1);
    case 'canonicalStatus':
      return (a.canonicalCheck?.status ?? -1) - (b.canonicalCheck?.status ?? -1);
    case 'externalLinkCount':
      return (a.externalLinks?.length ?? 0) - (b.externalLinks?.length ?? 0);
    case 'internalLinkCount':
      return (a.internalLinks?.length ?? -1) - (b.internalLinks?.length ?? -1);
  }
  // 型網羅のため
  return 0;
}

function statusColor(status: number, error?: string): string {
  if (error) return 'bg-red-100 text-red-700';
  if (status >= 500) return 'bg-red-100 text-red-700';
  if (status >= 400) return 'bg-red-100 text-red-700';
  if (status >= 300) return 'bg-amber-100 text-amber-700';
  if (status >= 200) return 'bg-emerald-100 text-emerald-700';
  return 'bg-slate-100 text-slate-700';
}

// URL 列のヘッダ。ソート可能 + 右端のハンドルで列幅をドラッグ変更
function UrlTh({
  sortKey,
  sortDir,
  toggle,
  width,
  onResizeStart,
}: {
  sortKey: SortKey;
  sortDir: SortDir;
  toggle: (k: SortKey) => void;
  width: number;
  onResizeStart: (startX: number) => void;
}) {
  const arrow = sortKey === 'url' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  return (
    <th
      style={{ width, minWidth: width, maxWidth: width }}
      className="sticky left-0 top-0 z-30 bg-slate-700 relative cursor-pointer select-none whitespace-nowrap px-2 py-1 text-left font-medium hover:bg-slate-600"
      onClick={() => toggle('url')}
    >
      URL{arrow}
      <span
        onMouseDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onResizeStart(e.clientX);
        }}
        onClick={(e) => e.stopPropagation()}
        className="absolute right-0 top-0 z-10 h-full w-1.5 cursor-col-resize hover:bg-blue-400"
        title="ドラッグで列幅を変更"
      />
    </th>
  );
}

// URL セル。truncate 表示。URL テキストをクリックで新規タブで開く（行クリックには伝播させない）。
// ホバーで Portal 経由のポップオーバーを即時表示し、別窓アイコン + URL 全文をリンクで提供。
// primaryHost が指定されていて URL の host が同じなら、テーブル上はドメインを省略してパスのみ表示
function UrlCell({ url, primaryHost }: { url: string; primaryHost?: string }) {
  const [hover, setHover] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const wrapRef = useRef<HTMLDivElement>(null);

  // 表示用 URL：クロール起点と同じホストならパス + クエリ + ハッシュだけ、別ホストは完全 URL
  const displayUrl = (() => {
    if (!primaryHost) return url;
    try {
      const u = new URL(url);
      if (u.host === primaryHost) {
        return (u.pathname || '/') + u.search + u.hash;
      }
      return url;
    } catch {
      return url;
    }
  })();

  const open = (): void => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ x: r.left, y: r.bottom + 4 });
    setHover(true);
  };
  const close = (): void => setHover(false);

  return (
    <>
      <div
        ref={wrapRef}
        onMouseEnter={open}
        onMouseLeave={close}
        className="truncate"
      >
        {/* URL テキスト全体がリンク。クリックで新規タブ、行クリックには伝播させない */}
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-slate-700 hover:text-slate-900 hover:underline"
        >
          {displayUrl}
        </a>
      </div>
      {hover &&
        createPortal(
          <div
            style={{ position: 'fixed', left: pos.x, top: pos.y, zIndex: 100, maxWidth: 640 }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={close}
            onClick={(e) => e.stopPropagation()}
            className="rounded-md border border-slate-300 bg-white p-3 shadow-xl"
          >
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="flex items-start gap-1.5 break-all font-mono text-xs text-slate-800 hover:text-blue-700 hover:underline"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0 text-slate-500">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              <span>{url}</span>
            </a>
          </div>,
          document.body,
        )}
    </>
  );
}

// 件数セルをクリックして全テキスト一覧をポップオーバー表示する。
// table が overflow-auto の中にあるため Portal で body 直下にレンダリングして見切れを回避
function PopoverCell({
  count,
  items,
  heading,
  emphasize,
  title,
}: {
  count: number;
  items: string[];
  heading: string;
  emphasize?: boolean;
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const POPOVER_WIDTH = 320;

  const positionPopover = (): void => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    let left = rect.right - POPOVER_WIDTH; // 右端を揃える
    if (left < 8) left = 8;
    if (left + POPOVER_WIDTH > window.innerWidth - 8) {
      left = window.innerWidth - POPOVER_WIDTH - 8;
    }
    setPos({ top: rect.bottom + 4, left });
  };

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent): void => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  if (count === 0) {
    return <span className="tabular-nums text-slate-400">0</span>;
  }

  const numColor = emphasize ? 'text-amber-700' : 'text-slate-800';
  const numFont = emphasize ? 'font-medium' : '';

  return (
    <>
      <button
        ref={triggerRef}
        onClick={(e) => {
          // 行クリックで詳細パネルを開く挙動と干渉させない
          e.stopPropagation();
          if (!open) positionPopover();
          setOpen((v) => !v);
        }}
        className={`group inline-flex items-baseline gap-1 rounded px-1.5 py-0.5 transition hover:bg-slate-200/70 ${
          open ? 'bg-slate-200/70' : ''
        }`}
        title={title}
      >
        <span className={`tabular-nums underline decoration-slate-300 decoration-dotted underline-offset-[3px] group-hover:decoration-slate-500 ${numColor} ${numFont}`}>
          {count}
        </span>
        <span className="text-[9px] text-slate-400 transition group-hover:text-slate-700">
          {open ? '▴' : '▾'}
        </span>
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={popoverRef}
            style={{ top: pos.top, left: pos.left, width: POPOVER_WIDTH }}
            className="fixed z-50 max-h-80 overflow-y-auto rounded-md border border-slate-300 bg-white shadow-lg"
          >
            <div className="flex items-baseline justify-between border-b border-slate-100 bg-slate-50 px-2 py-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                {heading}{' '}
                <span className="ml-1 tabular-nums font-normal text-slate-500">{count} 件</span>
              </span>
              <button
                onClick={() => setOpen(false)}
                className="text-xs text-slate-400 hover:text-slate-700"
                aria-label="閉じる"
              >
                ✕
              </button>
            </div>
            <ol className="divide-y divide-slate-100">
              {items.map((item, i) => (
                <li
                  key={i}
                  className="flex items-baseline gap-2 px-2 py-1 text-xs"
                >
                  <span className="shrink-0 w-5 text-right tabular-nums text-slate-400">
                    {i + 1}
                  </span>
                  <span className="flex-1 break-words text-slate-800">
                    {item ? (
                      item
                    ) : (
                      <em className="text-slate-400">(空テキスト)</em>
                    )}
                  </span>
                  <span className="shrink-0 tabular-nums text-[10px] text-slate-400">
                    {item.length}
                  </span>
                </li>
              ))}
            </ol>
          </div>,
          document.body,
        )}
    </>
  );
}

// `_TableFilter` を未使用 export 警告から守る（型再 export 用途）
export type _TableFilter = TableFilter;
